// Issue 62: "Fetch Instagram posts". Instagram is read ONLY when a person clicks here: no schedule,
// no automatic look-back, no retry. The button says why it is disabled and when it can be used again.
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { ErrorMsg, Loading, Section, Time, Unknown } from '../../components/ui';
import { api, errorText, newIdempotencyKey } from '../../lib/api';
import type { InstagramStatus } from '../../lib/deepTypes';
import { useApi } from '../../lib/session';
import type { Job, StudioDetail } from '../../lib/types';

const DONE = ['succeeded', 'partial', 'failed', 'cancelled'];

function summary(j: Job): { text: string; ok: boolean } {
  const o = j.outputs ?? {};
  if (j.status === 'succeeded') {
    const byHand = Number(o.already_by_hand ?? 0);
    return {
      ok: true,
      text: `Read ${o.read} post(s) of @${o.handle}: ${o.new_posts} new, ${o.seen_posts} already held${byHand ? `, ${byHand} already entered by hand` : ''}.`,
    };
  }
  return { ok: false, text: j.error ?? `The read ended ${j.status}.` };
}

export function InstagramSection({ studio, onDone }: { studio: StudioDetail; onDone?: () => void }) {
  const st = useApi<InstagramStatus>(`/api/studios/${studio.id}/instagram`);
  const [count, setCount] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [following, setFollowing] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ text: string; ok: boolean } | null>(null);
  const [err, setErr] = useState('');

  // Follow the queued job until it ends (also one that was already running when the page opened).
  const active = following ?? st.data?.active_job_id ?? null;
  useEffect(() => {
    if (!active) return;
    let stop = false;
    const tick = async () => {
      try {
        const j = await api<Job>(`/api/jobs/${active}`);
        if (stop) return;
        if (DONE.includes(j.status)) {
          setMsg(summary(j));
          setFollowing(null);
          st.reload();
          onDone?.();
          return;
        }
      } catch {
        /* keep polling */
      }
      if (!stop) setTimeout(tick, 3000);
    };
    const t = setTimeout(tick, 1500);
    return () => {
      stop = true;
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  async function fetchPosts() {
    if (!st.data) return;
    setBusy(true);
    setErr('');
    setMsg(null);
    try {
      const r = await api<{ job_id: string; created: boolean }>(`/api/studios/${studio.id}/instagram/fetch`, {
        body: { count: count ?? st.data.default_count },
        idempotencyKey: newIdempotencyKey(),
      });
      setFollowing(r.job_id);
    } catch (e) {
      setErr(errorText(e));
      st.reload();
    } finally {
      setBusy(false);
    }
  }

  const d = st.data;
  const running = !!active;
  return (
    <Section title="Instagram">
      {st.loading && !d ? (
        <Loading what="Loading Instagram status" />
      ) : !d ? (
        <ErrorMsg>{st.error}</ErrorMsg>
      ) : (
        <>
          <div className="row gap">
            <label className="small">
              Latest posts{' '}
              <select value={count ?? d.default_count} onChange={(e) => setCount(Number(e.target.value))} aria-label="Number of Instagram posts" disabled={running}>
                {d.counts.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              className="btn small"
              data-testid="ig-fetch"
              disabled={!d.can_fetch || busy || running}
              title={d.reason ?? 'Read the latest posts of this studio’s Instagram account, once, now'}
              onClick={() => void fetchPosts()}
            >
              {running ? 'Fetching…' : 'Fetch Instagram posts'}
            </button>
            {d.handle && <span className="muted small">@{d.handle}</span>}
          </div>
          {d.reason && !running && (
            <p className="small muted" data-testid="ig-reason">
              {d.reason}.
            </p>
          )}
          {running && (
            <p className="small">
              Reading Instagram… <Link to={`/jobs/${active}`}>job {active?.slice(0, 8)}</Link>
            </p>
          )}
          <p className="small muted">
            Latest post held from Instagram: {d.latest_post_at ? <Time iso={d.latest_post_at} /> : <Unknown>none yet</Unknown>}
          </p>
          {msg && <p className={`msg ${msg.ok ? 'ok' : 'info'} small`}>{msg.text}</p>}
          <ErrorMsg>{err}</ErrorMsg>
          <p className="muted small">
            Instagram is read only when you click, never on a schedule. Each click reads one profile page and the latest posts you chose (links, dates, captions,
            likes and comments when shown; no pictures). Instagram limits accounts that read too much: after a click there is a 10-minute pause for everyone, and if
            Instagram refuses, a 24-hour one. Fetched posts are kept next to the ones you enter by hand and are not part of the posting-rate figures.
          </p>
        </>
      )}
    </Section>
  );
}
